import { FC, useState, useEffect } from "react";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import axios from "axios";
import Swal from "sweetalert2";
import { getAuth } from "../../../../../modules/auth";

interface LocationState {
  moduleId?: number;
  championshipId?: string;
  championshipName?: string;
  returnTab?: string;
}

interface Categoria {
  _id_categoria: number;
  nome: string;
}

export const ConvocacaoForm: FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();
  const auth = getAuth();
  const apiUrl = process.env.REACT_APP_API_URL;

  const state = location.state as LocationState;
  const moduleId = state?.moduleId || 1;
  const preSelectedChampionshipId = state?.championshipId;
  const returnTab = state?.returnTab || "JOGOS";

  const [loading, setLoading] = useState(false);
  const [campeonatos, setCampeonatos] = useState<any[]>([]);
  const [categorias, setCategorias] = useState<Categoria[]>([]);

  const [formData, setFormData] = useState({
    titulo: "",
    _id_campeonato: preSelectedChampionshipId || "",
    _id_categoria: "",
    adversario_nome: "",
    valor_taxa: "",
    data_apresentacao: "",
    local: "",
    observacoes: "",
    adversario_logo: "",
  });

  const [adversarioLogoFile, setAdversarioLogoFile] = useState<File | null>(
    null,
  );

  const handleBack = () => {
    if (preSelectedChampionshipId) {
      navigate(
        `/technical-committee/championships/edit/${preSelectedChampionshipId}`,
        {
          state: { moduleId, initialTab: returnTab },
        },
      );
    } else {
      navigate(-1);
    }
  };

  useEffect(() => {
    const fetchAllData = async () => {
      if (!auth?.api_token) return;
      const headers = { Authorization: `Bearer ${auth.api_token}` };

      try {
        const [resCamp, resCat] = await Promise.all([
          axios.get(`${apiUrl}/technical-committee/championships`, {
            params: { moduleId },
            headers,
          }),
          axios.get(`${apiUrl}/technical-committee/categories`, { headers }),
        ]);

        setCampeonatos(
          resCamp.data.filter((c: any) => c.status !== "cancelado"),
        );
        setCategorias(resCat.data);

        if (id) {
          setLoading(true);
          const resConvo = await axios.get(
            `${apiUrl}/technical-committee/convocations/${id}`,
            { headers },
          );
          const data = resConvo.data;
          const dateFormatted = data.data_apresentacao
            ? new Date(data.data_apresentacao).toISOString().slice(0, 16)
            : "";

          setFormData({
            titulo: data.titulo,
            _id_campeonato: data._id_campeonato || "",
            _id_categoria: data._id_categoria || "",
            adversario_nome: data.adversario_nome || "",
            valor_taxa: data.valor_taxa || "",
            data_apresentacao: dateFormatted,
            local: data.local,
            observacoes: data.observacoes || "",
            adversario_logo: data.adversario_logo || "",
          });
          setLoading(false);
        }
      } catch (error) {
        console.error("Erro ao carregar dados", error);
      }
    };
    fetchAllData();
  }, [apiUrl, auth?.api_token, moduleId, id]);

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();

    if (
      !formData.titulo ||
      !formData.data_apresentacao ||
      !formData.local ||
      !formData._id_categoria
    ) {
      Swal.fire("Atenção", "Preencha os campos obrigatórios.", "warning");
      return;
    }

    setLoading(true);
    try {
      const payload = new FormData();
      payload.append("titulo", formData.titulo);
      payload.append("data_apresentacao", formData.data_apresentacao);
      payload.append("local", formData.local);
      payload.append("observacoes", formData.observacoes);
      payload.append("_id_modulo", String(moduleId));
      payload.append("_id_categoria", formData._id_categoria);

      if (formData._id_campeonato) {
        payload.append("_id_campeonato", formData._id_campeonato);
      }
      if (formData.adversario_nome) {
        payload.append("adversario_nome", formData.adversario_nome);
      }
      if (formData.valor_taxa) {
        payload.append("valor_taxa", formData.valor_taxa);
      }

      if (adversarioLogoFile) {
        payload.append("file", adversarioLogoFile);
      }

      const config = {
        headers: {
          Authorization: `Bearer ${auth?.api_token}`,
          "Content-Type": "multipart/form-data",
        },
      };

      if (id) {
        await axios.put(
          `${apiUrl}/technical-committee/convocations/${id}`,
          payload,
          config,
        );
        Swal.fire({
          icon: "success",
          title: "Jogo atualizado!",
          showConfirmButton: false,
          timer: 1500,
        });
      } else {
        const res = await axios.post(
          `${apiUrl}/technical-committee/convocations`,
          payload,
          config,
        );
        Swal.fire({
          icon: "success",
          title: "Jogo criado!",
          showConfirmButton: false,
          timer: 1500,
        });

        const newId = res.data.insertId || res.data._id_convocacao;
        if (newId) {
          navigate(`/technical-committee/convocations/edit/${newId}`, {
            state: {
              moduleId,
              championshipId: preSelectedChampionshipId,
              returnTab,
            },
            replace: true,
          });
        }
      }
    } catch (error) {
      console.error(error);
      Swal.fire("Erro", "Falha ao salvar.", "error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="card shadow-sm w-100 pt-0 mt-0">
      <div className="card-header">
        <h3 className="card-title">{id ? "Editar Jogo" : "Novo Jogo"}</h3>
        <div className="card-toolbar">
          <button
            type="button"
            className="btn btn-sm btn-light"
            onClick={handleBack}
          >
            Voltar
          </button>
        </div>
      </div>
      <div className="card-body w-100 pt-0 mt-0">
        <form onSubmit={handleSave}>
          <div className="row g-5 mb-8">
            <div className="col-md-5">
              <label className="form-label required">Título do Evento</label>
              <input
                type="text"
                className="form-control form-control-sm "
                value={formData.titulo}
                onChange={(e) =>
                  setFormData({ ...formData, titulo: e.target.value })
                }
              />
            </div>
            <div className="col-md-2">
              <label className="form-label required">Categoria Base</label>
              <select
                className="form-select form-select-sm "
                value={formData._id_categoria}
                onChange={(e) =>
                  setFormData({ ...formData, _id_categoria: e.target.value })
                }
              >
                <option value="">Selecione...</option>
                {categorias.map((c) => (
                  <option key={c._id_categoria} value={c._id_categoria}>
                    {c.nome}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-md-3">
              <label className="form-label">Vincular Campeonato</label>
              <select
                className="form-select form-select-sm "
                value={formData._id_campeonato}
                onChange={(e) =>
                  setFormData({ ...formData, _id_campeonato: e.target.value })
                }
                disabled={!!preSelectedChampionshipId}
                style={
                  preSelectedChampionshipId
                    ? { backgroundColor: "#e9ecef" }
                    : {}
                }
              >
                <option value="">Nenhum (Amistoso)</option>
                {campeonatos.map((c) => (
                  <option key={c._id_campeonato} value={c._id_campeonato}>
                    {c.nome}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-md-2">
              <label className="form-label required">Valor (R$)</label>
              <input
                type="text"
                className="form-control form-control-sm "
                value={formData.valor_taxa}
                onChange={(e) =>
                  setFormData({ ...formData, valor_taxa: e.target.value })
                }
                placeholder="0,00"
              />
            </div>
          </div>

          <div className="row g-5 mb-5">
            <div className="col-md-3">
              <label className="form-label">Nome do Adversário</label>
              <input
                type="text"
                className="form-control form-control-sm "
                value={formData.adversario_nome}
                onChange={(e) =>
                  setFormData({ ...formData, adversario_nome: e.target.value })
                }
              />
            </div>
            <div className="col-md-3">
              <label className="form-label required">Local</label>
              <input
                type="text"
                className="form-control form-control-sm "
                value={formData.local}
                onChange={(e) =>
                  setFormData({ ...formData, local: e.target.value })
                }
              />
            </div>
            <div className="col-md-3">
              <label className="form-label">Foto / Escudo</label>
              <input
                type="file"
                className="form-control form-control-sm border border-gray-300 w-100"
                style={{
                  opacity: 1,
                  height: "auto",
                  position: "relative",
                }}
                accept="image/*"
                onChange={(e) => {
                  if (e.target.files && e.target.files[0]) {
                    setAdversarioLogoFile(e.target.files[0]);
                  }
                }}
              />
            </div>
            <div className="col-md-3">
              <label className="form-label required">
                Data/Hora Apresentação
              </label>
              <input
                type="datetime-local"
                className="form-control form-control-sm "
                value={formData.data_apresentacao}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    data_apresentacao: e.target.value,
                  })
                }
              />
            </div>
          </div>

          <div className="row">
            <div className="col-12">
              <label className="form-label">Observações</label>
              <textarea
                className="form-control form-control-sm "
                rows={3}
                value={formData.observacoes}
                onChange={(e) =>
                  setFormData({ ...formData, observacoes: e.target.value })
                }
              ></textarea>
            </div>
          </div>

          <div className="d-flex justify-content-end mt-10">
            <button
              type="button"
              className="btn btn-light me-3 btn-sm"
              onClick={handleBack}
            >
              Voltar
            </button>
            <button
              type="submit"
              className="btn btn-primary btn-sm"
              disabled={loading}
            >
              {loading ? "Salvando..." : id ? "Atualizar Jogo" : "Criar Jogo"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
